/**
 * Offline Orders API client
 * Base path: service-orders/offline-orders/
 */

import axios from 'axios'
import { BASE_URL } from './api'
import {
  hasOrderAppointment,
  isServiceOrder,
  canBookOrderAppointment,
  normalizeOrderStatus,
  ORDER_STATUS,
} from './orderStatus'

const OFFLINE_ORDERS_PATH = 'service-orders/offline-orders/'
const ORDERS_PATH = 'service-orders/orders/'

const authHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

const formatApiError = (error) => {
  const data = error?.response?.data
  if (!data) return error?.message || 'Request failed'
  if (typeof data === 'string') return data
  if (data.error) return data.error
  if (data.detail) return data.detail
  if (data.message) return data.message
  const messages = Object.entries(data).flatMap(([field, value]) => {
    if (Array.isArray(value)) return value.map((msg) => `${field}: ${msg}`)
    if (typeof value === 'string') return [`${field}: ${value}`]
    return []
  })
  return messages.length ? messages.join(', ') : 'Request failed'
}

/** Normalize API shape → UI-friendly fields */
export const normalizeOfflineOrder = (order) => {
  if (!order) return null
  const rawStatus = order.appointment_status
  const appointmentStatus =
    !rawStatus || rawStatus === 'no_appointment' ? 'none' : rawStatus

  return {
    ...order,
    item_type: order.order_type || order.item_type,
    product_name: order.item_name || order.product_name,
    appointment_status: appointmentStatus,
  }
}

export const parseFarmerPhoneForInput = (phone) => {
  if (!phone) return ''
  const digits = String(phone).replace(/\D/g, '')
  if (digits.startsWith('233') && digits.length > 9) {
    return digits.slice(3)
  }
  if (digits.startsWith('0') && digits.length === 10) {
    return digits.slice(1)
  }
  return digits
}

export const isOfflineOrder = (order) => {
  if (!order) return false
  const source = String(order.order_source || '').toLowerCase()
  if (source === 'offline') return true
  const label = String(order.order_source_display || '').toLowerCase()
  return label.includes('offline')
}

export const orderForAppointment = (order) => {
  if (!order) return null
  const firstItem = order.items?.[0]
  return normalizeOfflineOrder({
    ...order,
    customer_name: order.customer_name || order.customer_fullname,
    customer_phone: order.customer_phone,
    product_name:
      order.product_name ||
      order.item_name ||
      firstItem?.product_name ||
      firstItem?.product?.name,
    item_type:
      order.item_type ||
      order.order_type ||
      firstItem?.item_type ||
      firstItem?.product?.item_type ||
      firstItem?.product?.product_type,
  })
}

export const getOrderSourceLabel = (order) => {
  if (!order) return 'N/A'
  if (order.order_source_display) return order.order_source_display
  if (order.order_source) {
    return String(order.order_source).replace(/_/g, ' ')
  }
  return 'N/A'
}

export const hasExistingAppointment = hasOrderAppointment

export const canBookAppointment = canBookOrderAppointment

export const getOfflineOrders = async ({ search, ordering = '-created_at' } = {}) => {
  const params = new URLSearchParams()
  if (search?.trim()) params.append('search', search.trim())
  if (ordering) params.append('ordering', ordering)

  const query = params.toString()
  const url = `${BASE_URL}${OFFLINE_ORDERS_PATH}${query ? `?${query}` : ''}`

  const res = await axios.get(url, { headers: authHeaders() })
  const data = res.data
  const list = Array.isArray(data) ? data : data.results || []
  return list.map(normalizeOfflineOrder)
}

export const getOfflineOrderById = async (id) => {
  const res = await axios.get(`${BASE_URL}${OFFLINE_ORDERS_PATH}${id}/`, {
    headers: authHeaders(),
  })
  return normalizeOfflineOrder(res.data)
}

export const createOfflineOrder = async (payload) => {
  const body = {
    order_type: payload.order_type || payload.item_type,
    product_id: payload.product_id,
    customer_name: payload.customer_name,
    order_date: payload.order_date,
    notes: payload.notes || '',
  }
  if (payload.customer_phone) {
    body.customer_phone = payload.customer_phone
  }
  if (body.order_type === 'product' && payload.quantity != null) {
    body.quantity = Number(payload.quantity) || 1
  }
  if (payload.farmer_id != null) {
    body.farmer_id = payload.farmer_id
  }

  const res = await axios.post(`${BASE_URL}${OFFLINE_ORDERS_PATH}`, body, {
    headers: authHeaders(),
  })
  return normalizeOfflineOrder(res.data)
}

export const createAppointmentForOrder = async (orderId, { date, time, message }) => {
  const payload = {
    date,
    time: time?.length === 5 ? `${time}:00` : time,
    message: message || '',
  }

  const res = await axios.post(
    `${BASE_URL}${ORDERS_PATH}${orderId}/create_appointment/`,
    payload,
    { headers: authHeaders() }
  )

  return res.data
}

export { formatApiError as formatOfflineOrderError }

export const formatOrderDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const getAppointmentStatusLabel = (status, order) => {
  if (order && !isServiceOrder(order)) return 'N/A'
  const normalized = normalizeOrderStatus(status)
  if (normalized === ORDER_STATUS.APPOINTMENT_BOOKED) {
    return 'Appointment Scheduled'
  }
  switch (status) {
    case 'scheduled':
    case 'appointment_booked':
      return 'Appointment Scheduled'
    case 'sent':
      return 'SMS Sent'
    case 'none':
    case 'no_appointment':
    default:
      return 'No Appointment'
  }
}

export const getAppointmentStatusColor = (status) => {
  switch (status) {
    case 'scheduled':
    case 'appointment_booked':
      return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    case 'sent':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    default:
      return 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
  }
}
